// @flow
import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { animated } from 'react-spring/web.cjs';
import { getTranslation } from '@ts-react-utils/i18n';
import {
  SIZES,
  SPACING,
  FONT_WEIGHTS,
  COLORS,
  typography
} from '@tablekit/theme';
import Spinner from '@tablekit/spinner';
import Button from '@tablekit/button';
import { margin } from '@tablekit/utils';
import Icon from '@tablekit/icon';
import { faMap } from '@fortawesome/pro-solid-svg-icons/faMap';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import {
  getPageLocale,
  getPageLocaleListSeparator
} from 'components/PageContext';
import { getCuisineTranslations } from 'components/CuisinesContext';
import LoadingSpan from 'components/LoadingSpan';
import type { BlokComponentPropsType } from 'components/types';
import isIOS from 'utils/isIOS';
import BaseCard, { FULL_TOP_IMAGE_CLASS, HOVER_SCALE } from 'styled/Card';
import type { TCApiShopType, CmsShopType } from './types';
import FooterContent from './Footer';
import { gridElementStyles, CardSeparator } from './styled';
import { useApiShop } from './apiShopsContext';
import { useGeoLocation } from './geoLocationContext';

type PropsType = BlokComponentPropsType<CmsShopType> & {
  apiShop?: TCApiShopType,
  defaultReserveLabel: string,
  style: any,
  utmSource: string
};

export const CARD_HEIGHT = 512;

const Card = animated(styled(BaseCard)`
  ${gridElementStyles};
  & a {
    &,
    &:visited,
    &:active,
    &:hover {
      text-decoration: none !important;
    }
  }

  &:hover ${CardSeparator} {
    transform: scale(${HOVER_SCALE});
  }
`);

const ImageContainer = styled.a`
  height: 210px;
  width: 100%;
  position: relative;
  overflow: hidden;
  display: block;
  ${({ isLoading, isError }) => {
    if (isLoading) {
      return css`
        &:after {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${COLORS.WHITE_TRANSLUCENT.L3};
          z-index: 2;
        }
      `;
    }
    if (isError) {
      return css`
        background: ${COLORS.GRAY.L5};
      `;
    }
    return '';
  }}
`;

const Image = styled.img`
  height: 100%;
  width: 100%;
  // only IE11 doesn't support this option
  // on IE11 some warping of the image will occur as it forces to above sizes
  object-fit: cover;
`;

const ImageLoadingSpinner = styled(Spinner)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: opacity 0.2s ease-in-out;
  opacity: ${({ isLoading }) => (isLoading ? 1 : 0)};
  z-index: 2;
`;

const Body = styled.div`
  min-height: 210px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 0%;
`;

const LinkedBody = styled.a`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 100%;
  color: ${COLORS.GRAY_TRANSLUCENT.L9};
  padding: ${SPACING.L4} ${SPACING.L4} 0;
`;

const ShopName = styled.div`
  ${typography('L5')};
  font-weight: ${FONT_WEIGHTS.SEMI_BOLD};
`;

const ShopNameSpan = styled(LoadingSpan)`
  min-width: 112px;
`;

const ImageFallback = styled(ShopName)`
  color: white;
  background: ${COLORS.PURPLE_TRANSLUCENT.L7};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const ShopLocationName = styled.div`
  ${typography('L1')};
  color: ${COLORS.GRAY_TRANSLUCENT.L6};
  margin-bottom: ${SPACING.L1};
`;

const Cuisines = styled.div`
  ${typography('L1')};
  color: ${COLORS.GRAY_TRANSLUCENT.L6};
  margin-bottom: ${SPACING.L1};
`;

const CuisinesContent = styled(LoadingSpan)`
  min-width: 184px;
`;

const ShopBlurb = styled.div`
  ${typography('L3')};
  margin-bottom: ${SPACING.L5};
`;

const ShopBlurbSpan = styled(LoadingSpan)`
  min-width: 100%;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding: 0 ${SPACING.L4} ${SPACING.L4};
`;
const MenuItemButton = styled(Button)`
  flex: 1 1 0%;
  ${margin({ right: `${SPACING.L5} !important` })}
`;

const ShopCard = ({
  style,
  utmSource: unformattedUtmSource,
  defaultReserveLabel,
  blok: {
    slug,
    optional_summary: optionalSummary,
    custom_image: customImage,
    tags,
    menu_name: menuButtonName,
    menu_list: menuListId
  }
}: PropsType) => {
  const apiShop = useApiShop(slug);
  const { isFindingCurrentLocation, currentLocation } = useGeoLocation();
  const {
    cuisines = [],
    geocode,
    google_place_cid: googlePlaceCid,
    address_translations: addressTranslations,
    tagline_translations: taglineTranslations,
    location_name_translations: locationNameTranslations,
    image_url: searchImage,
    name_translations: nameTranslations,
    content_title_translations: contentTitleTranslations,
    tablecheck_booking_url: bookingUrl,
    tablecheck_shop_url: shopUrl
  } = apiShop || {};
  const [imageSrc, setImageSrc] = useState(
    customImage?.filename || searchImage
  );
  const [isImageLoading, setIsImageLoading] = useState(!!imageSrc);
  const [isImageError, setIsImageError] = useState(!imageSrc);
  const utmSource = unformattedUtmSource
    .toLowerCase()
    .replace(/[^a-z0-9_]/gi, '_');

  useEffect(() => {
    const newImageSrc = customImage?.filename || searchImage;
    if (newImageSrc === imageSrc) return;
    setImageSrc(newImageSrc);
    setIsImageLoading(!!newImageSrc);
    setIsImageError(!newImageSrc);
  }, [
    customImage,
    searchImage,
    imageSrc,
    setIsImageLoading,
    setIsImageError,
    setImageSrc
  ]);

  const locale = getPageLocale();
  const localeListSeparator = getPageLocaleListSeparator();
  const locationName = getTranslation(locationNameTranslations, locale);
  let name = getTranslation(nameTranslations, locale);
  if (!name && apiShop && apiShop.isMissing) {
    name = slug
      .split('-')
      .map((string = '') => string.slice(0, 1).toUpperCase() + string.slice(1))
      .join(' ');
  }
  const tagline = getTranslation(taglineTranslations, locale);
  const contentTitle = getTranslation(contentTitleTranslations, locale);

  const address = getTranslation(addressTranslations, locale);
  const urlSafeAddress = address.replace(/&/gi, encodeURIComponent('&'));
  let mapsLink = '';
  if (isIOS) {
    mapsLink = `https://maps.apple.com/q=${urlSafeAddress}`;
    if (geocode && geocode.lat && geocode.lon) {
      mapsLink += `&sll=${geocode.lat},${geocode.lon}`;
    }
  } else {
    mapsLink = `https://maps.google.com/?q=${urlSafeAddress}`;
    if (googlePlaceCid) {
      mapsLink += `&query_place_id=${googlePlaceCid}`;
    }
  }

  return (
    <Card
      useLinkAnimation
      style={style}
      onMouseDown={(event) => {
        // if in storyblok editor prevent clicks
        if (window.storyblok) {
          event.stopPropagation();
          event.preventDefault();
        }
      }}
    >
      <ImageContainer
        href={`${shopUrl}?utm_source=${utmSource}`}
        className={FULL_TOP_IMAGE_CLASS}
        isLoading={isImageLoading}
        isError={isImageError}
      >
        {isImageError ? (
          <ImageFallback>{name}</ImageFallback>
        ) : (
          <Image
            src={imageSrc}
            alt={customImage?.alt || name}
            ref={(imageRef) => {
              // this is a catch for SSR loading state where the image loads faster than the script
              if (imageRef && imageRef.complete && isImageLoading) {
                setIsImageLoading(false);
              }
            }}
            onLoad={() => setIsImageLoading(false)}
            onError={() => {
              setIsImageLoading(false);
              setIsImageError(true);
            }}
          />
        )}
        <ImageLoadingSpinner isLoading={isImageLoading} size={SIZES.LARGE} />
      </ImageContainer>
      <Body>
        <LinkedBody href={`${shopUrl}?utm_source=${utmSource}`}>
          <ShopName>
            <ShopNameSpan isLoading={!apiShop}>{name}</ShopNameSpan>
          </ShopName>
          <ShopLocationName>{locationName}</ShopLocationName>
          <Cuisines>
            <CuisinesContent isLoading={!apiShop}>
              {getCuisineTranslations(cuisines).join(localeListSeparator)}
            </CuisinesContent>
          </Cuisines>
          <ShopBlurb>
            <ShopBlurbSpan isLoading={!apiShop}>
              {optionalSummary || contentTitle || tagline}
            </ShopBlurbSpan>
          </ShopBlurb>
        </LinkedBody>
        <ActionsRow>
          <MenuItemButton
            iconAfter={<Icon icon={faChevronRight} />}
            isLoading={!apiShop}
            href={`${bookingUrl || ''}?${
              menuListId ? `menu_lists[]=${menuListId}&` : ''
            }utm_source=${utmSource}`}
          >
            {menuButtonName || defaultReserveLabel}
          </MenuItemButton>
          {urlSafeAddress ? (
            <Button
              href={mapsLink}
              target="_blank"
              rel="noreferrer"
              appearance="outline"
              iconAfter={<Icon size={SIZES.LARGE} icon={faMap} />}
            />
          ) : (
            <div style={{ width: 40 }} />
          )}
        </ActionsRow>
      </Body>
      <FooterContent
        href={`${shopUrl}?utm_source=${utmSource}`}
        tags={tags}
        isFindingCurrentLocation={isFindingCurrentLocation}
        currentLocation={currentLocation}
        apiShop={apiShop}
      />
    </Card>
  );
};

export default ShopCard;
